import React, { useState } from 'react';
import { X, GitBranch, ExternalLink, Pencil, Trash2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import '../../component-styles/RecordPages.css';

const TYPE_LABELS = {
  ffe: 'FF&E',
  finish: 'Finishes',
  plumbing: 'Plumbing',
  electrical: 'Electrical',
  lighting: 'Lighting',
  millwork: 'Millwork',
  appliance: 'Appliances',
  other: 'Other',
};

const DATE_FIELDS = [
  { key: 'targetDate', label: 'Target Date' },
  { key: 'orderedDate', label: 'Ordered Date' },
  { key: 'estimatedDeliveryDate', label: 'Est. Delivery' },
  { key: 'actualDeliveryDate', label: 'Actual Delivery' },
  { key: 'installedDate', label: 'Installed' },
  { key: 'backorderedDate', label: 'Back Order Date' },
];

function formatDate(value) {
  if (!value) return null;
  const d = new Date(`${value}T00:00:00`);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

export default function ScheduleItemFolioModal({ item, schedules = [], projectId, onClose, onEdit, onDelete }) {
  const [confirmDelete, setConfirmDelete] = useState(false);

  if (!item) return null;

  const scheduleName = item.scheduleId
    ? schedules.find((s) => s.sk === item.scheduleId)?.name
    : null;

  const source = item.linkedInvoiceId
    ? {
        label: item.linkedInvoiceNumber || 'Invoice',
        type: 'Invoice',
        path: `/projects/${projectId}/invoices/${item.linkedInvoiceId}`,
      }
    : item.linkedPoId
      ? {
          label: item.linkedPoNumber || 'Purchase Order',
          type: 'Purchase Order',
          path: `/projects/${projectId}/purchase-orders/${item.linkedPoId}`,
        }
      : item.linkedProposalId
        ? {
            label: 'Proposal',
            type: 'Proposal',
            path: `/projects/${projectId}/proposals/${item.linkedProposalId}`,
          }
        : null;

  const dateRows = DATE_FIELDS.map((f) => ({ ...f, value: formatDate(item[f.key]) })).filter(
    (f) => f.value
  );

  return (
    <div className="folio-overlay" onClick={onClose}>
      <div className="folio-modal" onClick={(e) => e.stopPropagation()}>
        <button className="folio-close" onClick={onClose}>
          <X size={20} />
        </button>

        <h3 className="folio-title">
          {item.name}
          {scheduleName && <span className="folio-sub">{scheduleName}</span>}
        </h3>

        {(onEdit || onDelete) && (
          <div className="folio-item-actions">
            {onEdit && (
              <button
                className="folio-action-btn folio-action-edit"
                onClick={() => {
                  onClose?.();
                  onEdit(item);
                }}
              >
                <Pencil size={14} />
                Edit
              </button>
            )}
            {onDelete && (
              <button
                className="folio-action-btn folio-action-delete"
                onClick={() => setConfirmDelete(true)}
              >
                <Trash2 size={14} />
                Delete
              </button>
            )}
          </div>
        )}

        {confirmDelete && (
          <div className="folio-delete-confirm">
            <p>Delete this schedule item? This cannot be undone.</p>
            <div className="folio-delete-confirm-actions">
              <button onClick={() => setConfirmDelete(false)}>Cancel</button>
              <button
                className="folio-delete-confirm-yes"
                onClick={() => {
                  onDelete(item.sk);
                  onClose?.();
                }}
              >
                Yes, Delete
              </button>
            </div>
          </div>
        )}

        <div className="folio-summary">
          <div>
            <label>Room</label>
            <div>{item.room || 'Unassigned'}</div>
          </div>
          <div>
            <label>Type</label>
            <div>{TYPE_LABELS[item.scheduleType] || 'FF&E'}</div>
          </div>
          {item.vendor && (
            <div>
              <label>Vendor</label>
              <div>{item.vendor}</div>
            </div>
          )}
          <div>
            <label>Qty / Unit</label>
            <div>
              {item.qty || 1} {item.unitType || 'Each'}
            </div>
          </div>
          {item.measurement && (
            <div>
              <label>Measurement</label>
              <div>{item.measurement}</div>
            </div>
          )}
          {item.leadTime && (
            <div>
              <label>Lead Time</label>
              <div>{item.leadTime}</div>
            </div>
          )}
          <div>
            <label>Status</label>
            <div>{item.status || 'Draft'}</div>
          </div>
          <div>
            <label>CFA Approved</label>
            <div>{item.cfaApproved ? 'Yes' : 'No'}</div>
          </div>
        </div>

        {item.description && (
          <>
            <h4 className="folio-section-label">Description</h4>
            <p>{item.description}</p>
          </>
        )}

        {dateRows.length > 0 && (
          <>
            <h4 className="folio-section-label">Dates</h4>
            <div className="folio-summary">
              {dateRows.map((f) => (
                <div key={f.key}>
                  <label>{f.label}</label>
                  <div>{f.value}</div>
                </div>
              ))}
            </div>
          </>
        )}

        {item.notes && (
          <>
            <h4 className="folio-section-label">Notes</h4>
            <p>{item.notes}</p>
          </>
        )}

        <h4 className="folio-section-label">Source</h4>
        <div className="folio-applied-list">
          {source ? (
            <Link to={source.path} className="folio-applied-row">
              <GitBranch size={16} className="folio-row-icon" />
              <div className="folio-row-text">
                <b>{source.label}</b> - {source.type}
              </div>
              <ExternalLink size={14} className="folio-open-icon" />
            </Link>
          ) : (
            <div className="folio-empty">Added manually.</div>
          )}
        </div>
      </div>
    </div>
  );
}
